import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MiniPage } from "@/components/MiniPage";
import { ExpiryBadge } from "@/components/ExpiryBadge";
import { resolveLayout } from "@/lib/pinLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Plus, MoreVertical, Copy, Pencil, Trash2, FileText, LogOut, Download, CheckSquare, SlidersHorizontal, Palette, Languages, Bell,
} from "lucide-react";

function loadDashboardMidtransSnap(clientKey, snapUrl) {
  const source = snapUrl || "https://app.sandbox.midtrans.com/snap/snap.js";
  let parsed;
  try { parsed = new URL(source); } catch { throw new Error("URL Midtrans tidak valid."); }
  const allowedOrigins = new Set([
    "https://app.sandbox.midtrans.com",
    "https://app.midtrans.com",
  ]);
  if (!allowedOrigins.has(parsed.origin) || parsed.pathname !== "/snap/snap.js") {
    throw new Error("Sumber pembayaran Midtrans tidak diizinkan.");
  }

  const existing = document.querySelector('script[data-pms-midtrans="true"]');
  if (window.snap) {
    if (!existing || (existing.src === parsed.href && existing.dataset.clientKey === clientKey)) {
      return Promise.resolve();
    }
    return Promise.reject(new Error("Lingkungan pembayaran berubah. Muat ulang halaman."));
  }
  if (existing) {
    if (existing.src !== parsed.href || existing.dataset.clientKey !== clientKey) {
      return Promise.reject(new Error("Lingkungan pembayaran tidak cocok. Muat ulang halaman."));
    }
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => window.snap ? resolve() : reject(new Error("Midtrans Snap gagal dimuat.")), { once: true });
      existing.addEventListener("error", () => reject(new Error("Tidak dapat memuat pembayaran Midtrans.")), { once: true });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = parsed.href;
    script.dataset.clientKey = clientKey;
    script.dataset.pmsMidtrans = "true";
    script.async = true;
    script.onload = () => window.snap ? resolve() : reject(new Error("Midtrans Snap gagal dimuat."));
    script.onerror = () => reject(new Error("Tidak dapat memuat pembayaran Midtrans."));
    document.head.appendChild(script);
  });
}

function ProjectThumb({ project }) {
  const ref = useRef(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setW(e.contentRect.width);
    });
    ro.observe(el);
    setW(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  const s = project.settings || {};
  const layout = resolveLayout(s.innerD ?? 45, s.outerD ?? 55.5, s.gridPreset || "44", s.gapH ?? 0, s.gapV ?? 3);
  const page = { cells: project.first_page || [] };
  return (
    <div ref={ref} className="w-full" style={{ aspectRatio: "210/297" }}>
      {w > 0 && page.cells.length > 0 && (
        <MiniPage
          page={page}
          layout={layout}
          projectId={project.id}
          width={w}
          showCenter={s.showCenter}
          centerDotSize={s.centerDotSize}
          centerDotColor={s.centerDotColor}
        />
      )}
    </div>
  );
}

  const defaultPresetSettings = {
    "25": { innerD: 26, outerD: 36.5, gapH: 0, gapV: 3, showCenter: false, centerDotSize: 1.5, centerDotColor: "#e22020" },
    "32": { innerD: 33, outerD: 43.5, gapH: 0, gapV: 3, showCenter: false, centerDotSize: 1.5, centerDotColor: "#e22020" },
    "44": { innerD: 45, outerD: 55.5, gapH: 0, gapV: 3, showCenter: false, centerDotSize: 1.5, centerDotColor: "#e22020" },
    "58": { innerD: 59, outerD: 71.5, gapH: 0, gapV: 3, showCenter: false, centerDotSize: 1.5, centerDotColor: "#e22020" },
    "75": { innerD: 76, outerD: 87.5, gapH: 0, gapV: 3, showCenter: false, centerDotSize: 1.5, centerDotColor: "#e22020" },
  };

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [installPrompt, setInstallPrompt] = useState(null);

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setInstallPrompt(e); };
    const onInstalled = () => setInstallPrompt(null);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) {
      toast.info("Buka menu browser lalu pilih 'Install' / 'Add to Home screen'");
      return;
    }
    installPrompt.prompt();
    try { await installPrompt.userChoice; } catch (e) { console.error("PWA install prompt failed:", e); }
    setInstallPrompt(null);
  };
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const projectsLoadedRef = useRef(false);
  const [search, setSearch] = useState("");
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [presetDialogOpen, setPresetDialogOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState("44");
  const [presetSettingsOpen, setPresetSettingsOpen] = useState(false);
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const [settingsGearRotation, setSettingsGearRotation] = useState(0);
  const settingsOpenDelayRef = useRef(null);

  const handleSettingsMenuOpenChange = useCallback((open) => {
    if (open) {
      if (settingsMenuOpen || settingsOpenDelayRef.current) return;

      setSettingsGearRotation((rotation) => rotation + 360);

      settingsOpenDelayRef.current = setTimeout(() => {
        settingsOpenDelayRef.current = null;
        setSettingsMenuOpen(true);
      }, 200);
      return;
    }

    if (!settingsMenuOpen) return;

    setSettingsGearRotation((rotation) => rotation - 360);
    setSettingsMenuOpen(false);
  }, [settingsMenuOpen]);

  useEffect(() => {
    if (!settingsMenuOpen) return undefined;

    const timer = setTimeout(() => {
      handleSettingsMenuOpenChange(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, [settingsMenuOpen, handleSettingsMenuOpenChange]);

  useEffect(() => {
    return () => {
      if (settingsOpenDelayRef.current) {
        clearTimeout(settingsOpenDelayRef.current);
      }
    };
  }, []);
  const [orderUpdatesOpen, setOrderUpdatesOpen] = useState(false);

  const [lastOrderId, setLastOrderId] = useState(
    () => localStorage.getItem("pinmaker-last-order-id") || ""
  );
  const [lastOrder, setLastOrder] = useState(null);
  const [orderStatusLoading, setOrderStatusLoading] = useState(false);
  const [orderStatusError, setOrderStatusError] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [renewalModalOpen, setRenewalModalOpen] = useState(false);
  const [renewalStep, setRenewalStep] = useState("email");
  const [renewalEmailRequired, setRenewalEmailRequired] = useState(false);
  const [renewalEmailInput, setRenewalEmailInput] = useState("");
  const [renewalEmailHint, setRenewalEmailHint] = useState("");
  const [renewalOtp, setRenewalOtp] = useState("");
  const [renewalToken, setRenewalToken] = useState("");
  const [renewalBusy, setRenewalBusy] = useState(false);
  const [renewalPricing, setRenewalPricing] = useState(null);
  const [renewalAutoStart, setRenewalAutoStart] = useState(false);
  const [themeSettingsOpen, setThemeSettingsOpen] = useState(false);
  const [languageSettingsOpen, setLanguageSettingsOpen] = useState(false);
  const [theme, setTheme] = useState(() => {
    try {
      const shared = localStorage.getItem("pms-theme");
      if (shared === "dark" || shared === "light") return shared;

      const legacy = localStorage.getItem("pinmaker-login-theme");
      if (legacy === "dark" || legacy === "light") return legacy;

      return "dark";
    } catch {
      return "dark";
    }
  });

  // PMS_SHARED_THEME_SYNC_DASHBOARD_V1
  useEffect(() => {
    const syncTheme = (event) => {
      const next = event?.detail?.theme;
      if (next === "dark" || next === "light") {
        setTheme(current => current === next ? current : next);
      }
    };
    window.addEventListener("pms-theme-change", syncTheme);
    return () => window.removeEventListener("pms-theme-change", syncTheme);
  }, []);

  const [language, setLanguage] = useState(() => localStorage.getItem("pinmaker-login-language") || "id");
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("pms-theme", theme);
    localStorage.setItem("pinmaker-login-theme", theme);
    window.dispatchEvent(
      new CustomEvent("pms-theme-change", { detail: { theme } })
    );
    if (settingsLoaded) {
      api.put("/user/settings", { theme }).catch(() => {
        toast.error("Tema gagal disimpan ke akun");
      });
    }
  }, [theme, settingsLoaded]);

  useEffect(() => {
    localStorage.setItem("pinmaker-login-language", language);
  }, [language]);

  useEffect(() => {
    api.get("/store/renewal/pricing")
      .then((response) => setRenewalPricing(response.data?.packages || null))
      .catch(() => {});
  }, []);

  const dashboardPlanPricing = (planId, fallbackPrice, fallbackDays) => {
    const item = renewalPricing?.[planId] || {};
    const originalPrice = Number(item.original_price ?? fallbackPrice);
    const discountPrice = item.discount_price == null ? null : Number(item.discount_price);
    const discountActive = Boolean(
      item.discount_active && Number.isFinite(discountPrice) &&
      discountPrice > 0 && discountPrice < originalPrice
    );
    return {
      price: discountActive ? discountPrice : Number(item.price ?? originalPrice),
      originalPrice,
      discountActive,
      days: Number(item.days ?? fallbackDays),
    };
  };

  const dashboardRupiah = (value) =>
    `Rp${Math.max(0, Number(value) || 0).toLocaleString("id-ID")}`;

  useEffect(() => {
    let stopped = false;
    let timer;

    const checkOrder = async () => {
      const savedOrderId = localStorage.getItem("pinmaker-last-order-id") || "";
      if (savedOrderId !== lastOrderId) setLastOrderId(savedOrderId);

      if (!savedOrderId) {
        if (!stopped) {
          setLastOrder(null);
          setOrderStatusLoading(false);
        }
        return;
      }

      try {
        const response = await api.get(
          "/store/order-status/" + encodeURIComponent(savedOrderId)
        );
        if (stopped) return;
        setLastOrder({ ...response.data, order_id: savedOrderId });
        setOrderStatusError(false);
      } catch (_) {
        if (!stopped) setOrderStatusError(true);
      } finally {
        if (!stopped) setOrderStatusLoading(false);
      }

      if (!stopped) timer = setTimeout(checkOrder, 4000);
    };

    setOrderStatusLoading(true);
    checkOrder();

    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [lastOrderId]);

  useEffect(() => {
    let active = true;
    const loadNotifications = async () => {
      try {
        const response = await api.get("/customer/notifications");
        if (!active) return;
        setNotifications(response.data.notifications || []);
        setUnreadNotificationCount(response.data.unread_count || 0);
      } catch (error) {
        if (error?.response?.status !== 401) {
          console.error("Gagal memuat notifikasi Dashboard:", error);
        }
      }
    };
    loadNotifications();
    const timer = setInterval(loadNotifications, 30000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    const clearLocalNotifications = () => {
      setNotifications([]);
      setUnreadNotificationCount(0);
    };

    const handleStorage = (event) => {
      if (event.key === "pinmaker-notifications-cleared") {
        clearLocalNotifications();
      }
    };

    window.addEventListener("pinmaker-notifications-cleared", clearLocalNotifications);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("pinmaker-notifications-cleared", clearLocalNotifications);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const clearAllNotifications = async () => {
    if (!notifications.length) return;
    if (!window.confirm(language === "en"
      ? "Clear all notifications? Payment orders and API Keys will remain unchanged."
      : "Bersihkan semua notifikasi? Pesanan pembayaran dan API Key tidak akan berubah.")) return;
    try {
      await api.post("/customer/notifications/clear-all");
      setNotifications([]);
      setUnreadNotificationCount(0);
      window.dispatchEvent(new Event("pinmaker-notifications-cleared"));
      try {
        localStorage.setItem("pinmaker-notifications-cleared", String(Date.now()));
      } catch (storageError) {
        console.warn("Gagal menyinkronkan notifikasi antartab:", storageError);
      }
      toast.success(language === "en" ? "All notifications cleared." : "Semua notifikasi berhasil dibersihkan.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || (language === "en" ? "Could not clear notifications." : "Notifikasi gagal dibersihkan."));
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await api.post(`/customer/notifications/${notificationId}/read`);
      const readAt = new Date().toISOString();
      setNotifications((items) => items.map((item) =>
        item.id === notificationId ? { ...item, read_at: readAt } : item
      ));
      setUnreadNotificationCount((count) => Math.max(0, count - 1));
    } catch (error) {
      toast.error(language === "en"
        ? "Could not mark notification as read"
        : "Notifikasi gagal ditandai sudah dibaca");
    }
  };

  const resumePendingRenewalPayment = (item) => {
    if (!item?.redirect_url) {
      toast.error(language === "en" ? "Payment link is unavailable." : "Tautan pembayaran tidak tersedia.");
      return;
    }
    window.open(item.redirect_url, "_blank", "noopener,noreferrer");
  };

  const cancelPendingRenewalPayment = async (item) => {
    if (!item?.order_id) return;
    const confirmed = window.confirm(language === "en"
      ? "Cancel this pending payment? You can choose a package again after Midtrans confirms the cancellation."
      : "Batalkan pesanan yang belum dibayar ini? Kamu dapat memilih paket kembali setelah Midtrans mengonfirmasi pembatalan.");
    if (!confirmed) return;
    try {
      await api.post(`/customer/renewal-orders/${encodeURIComponent(item.order_id)}/cancel`);
      const refreshed = await api.get("/customer/notifications");
      setNotifications(refreshed.data.notifications || []);
      setUnreadNotificationCount(refreshed.data.unread_count || 0);
      toast.success(language === "en" ? "Pending order cancelled." : "Pesanan Pending berhasil dibatalkan.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || (language === "en"
        ? "Midtrans has not confirmed cancellation. Do not create another payment yet."
        : "Midtrans belum mengonfirmasi pembatalan. Jangan membuat pembayaran baru dulu."));
    }
  };

  const beginDashboardRenewalVerification = useCallback(async (email = "") => {
    setRenewalBusy(true);
    setRenewalModalOpen(true);
    try {
      const response = await api.post("/store/renewal/request-otp-session", {
        ...(String(email || "").trim() ? { email: String(email).trim().toLowerCase() } : {}),
      });
      if (response.data?.email_required) {
        setRenewalEmailRequired(true);
        setRenewalStep("email");
        return;
      }
      setRenewalEmailRequired(false);
      setRenewalEmailHint(response.data?.email_hint || "email pemilik");
      setRenewalOtp("");
      setRenewalToken("");
      setRenewalStep("otp");
      toast.success(language === "en" ? "Verification code sent to the owner email." : "Kode verifikasi dikirim ke email pemilik.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || (language === "en" ? "Could not send the verification code." : "Kode verifikasi belum dapat dikirim."));
    } finally {
      setRenewalBusy(false);
    }
  }, [language]);

  const verifyDashboardRenewalOtp = async () => {
    if (!/^\d{6}$/.test(renewalOtp.trim())) {
      toast.error(language === "en" ? "Enter the 6-digit code." : "Masukkan kode OTP 6 angka.");
      return;
    }
    setRenewalBusy(true);
    try {
      const response = await api.post("/store/renewal/verify-otp-session", {
        otp: renewalOtp.trim(),
      });
      setRenewalToken(response.data?.renewal_token || "");
      setRenewalStep("plans");
      toast.success(language === "en" ? "API Key ownership verified." : "Kepemilikan API Key berhasil diverifikasi.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || (language === "en" ? "Verification failed." : "Verifikasi gagal."));
    } finally {
      setRenewalBusy(false);
    }
  };

  const startDashboardRenewalCheckout = async (planId) => {
    if (!renewalToken || renewalBusy) return;
    setRenewalBusy(true);
    try {
      const response = await api.post("/store/renewal/checkout", {
        plan: planId,
        renewal_token: renewalToken,
      });
      const checkout = response.data || {};
      if (!checkout.snap_token || !checkout.client_key || !checkout.order_id) {
        throw new Error(language === "en" ? "Incomplete payment information." : "Data pembayaran tidak lengkap.");
      }
      localStorage.setItem("pinmaker-last-order-id", checkout.order_id);
      setLastOrderId(checkout.order_id);
      if (checkout.resume_token) {
        localStorage.setItem("pinmaker-resume-token-" + checkout.order_id, checkout.resume_token);
      }
      setRenewalToken("");
      await loadDashboardMidtransSnap(checkout.client_key, checkout.snap_url);
      window.snap.pay(checkout.snap_token, {
        onSuccess: () => {
          setRenewalModalOpen(false);
          setOrderUpdatesOpen(true);
          toast.info(language === "en" ? "Payment received. Waiting for server verification." : "Pembayaran diterima. Menunggu verifikasi server.");
        },
        onPending: () => {
          setRenewalModalOpen(false);
          setOrderUpdatesOpen(true);
          toast.info(language === "en" ? "Payment is pending." : "Pembayaran masih menunggu.");
        },
        onError: () => {
          setRenewalModalOpen(false);
          setOrderUpdatesOpen(true);
          toast.error(language === "en" ? "Payment could not be completed." : "Pembayaran belum berhasil.");
        },
        onClose: () => {
          setRenewalModalOpen(false);
          setOrderUpdatesOpen(true);
        },
      });
    } catch (error) {
      setRenewalToken("");
      setRenewalOtp("");
      setRenewalStep("otp");
      toast.error(error?.response?.data?.detail || error?.message || (language === "en" ? "Renewal checkout failed. Verify again." : "Checkout perpanjangan gagal. Verifikasi ulang."));
    } finally {
      setRenewalBusy(false);
    }
  };

  const startRenewalFromNotification = (notificationId) => {
    if (notificationId) markNotificationRead(notificationId);
    setOrderUpdatesOpen(false);
    setRenewalEmailRequired(false);
    setRenewalEmailInput("");
    setRenewalStep("email");
    setRenewalModalOpen(true);
    beginDashboardRenewalVerification();
  };

  useEffect(() => {
    if (localStorage.getItem("pms-renewal-open") !== "1") return;
    localStorage.removeItem("pms-renewal-open");
    setRenewalAutoStart(true);
  }, []);

  useEffect(() => {
    if (!renewalAutoStart) return;
    setRenewalAutoStart(false);
    setOrderUpdatesOpen(false);
    setRenewalEmailRequired(false);
    setRenewalEmailInput("");
    setRenewalEmailHint("");
    setRenewalOtp("");
    setRenewalToken("");
    setRenewalStep("email");
    setRenewalModalOpen(true);
    beginDashboardRenewalVerification();
  }, [renewalAutoStart, beginDashboardRenewalVerification]);

  const orderStatusText = (status) => {
    const value = String(status || "").toLowerCase();
    if (value === "paid") return language === "en" ? "Payment successful" : "Pembayaran berhasil";
    if (["failed", "cancel", "cancelled", "expire", "expired", "checkout_error"].includes(value)) {
      return language === "en" ? "Payment failed" : "Pembayaran gagal";
    }
    return language === "en" ? "Payment being processed" : "Pembayaran sedang diproses";
  };

  const [presetSettings, setPresetSettings] = useState(defaultPresetSettings);

  useEffect(() => {
    let mounted = true;

    api.get("/user/settings").then(({ data }) => {
      if (!mounted) return;

      if (data.theme === "light" || data.theme === "dark") {
        if (
          (data.theme === "dark" || data.theme === "light") &&
          !["dark", "light"].includes(localStorage.getItem("pms-theme"))
        ) {
          setTheme(data.theme);
        }
      }

      if (data.pinPresets && typeof data.pinPresets === "object") {
        setPresetSettings((current) => Object.fromEntries(
          Object.entries(defaultPresetSettings).map(([size, defaults]) => [
            size,
            { ...defaults, ...(data.pinPresets[size] || {}) }
          ])
        ));
      } else if (data.pinPreset && typeof data.pinPreset === "object") {
        const size = String(data.pinPreset.gridPreset || "44");
        if (defaultPresetSettings[size]) {
          setPresetSettings((current) => ({
            ...current,
            [size]: { ...current[size], ...data.pinPreset }
          }));
        }
      }

      setSettingsLoaded(true);
    }).catch(() => {
      if (mounted) toast.error("Pengaturan akun gagal dimuat; perubahan belum disimpan");
    });

    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!settingsLoaded) return undefined;

    const timer = setTimeout(() => {
      api.put("/user/settings", { pinPresets: presetSettings }).catch(() => {
        toast.error("Preset Pin gagal disimpan ke akun");
      });
    }, 600);

    return () => clearTimeout(timer);
  }, [presetSettings, settingsLoaded]);

  const updatePresetSetting = (size, field, value) => {
    setPresetSettings((current) => ({
      ...current,
      [size]: { ...current[size], [field]: value },
    }));
  };

  const [selectedProjectIds, setSelectedProjectIds] = useState([]);
  const [deleteMode, setDeleteMode] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
const load = useCallback(async () => {
    const isFirstLoad = !projectsLoadedRef.current;
    if (isFirstLoad) setLoading(true);

    const startedAt = performance.now();

    try {
      const res = await api.get("/projects");
      const data = res.data;
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.projects)
          ? data.projects
          : [];

      setProjects(list);
      projectsLoadedRef.current = true;

      console.info(
        "[Dashboard timing] Projects API:",
        Math.round(performance.now() - startedAt),
        "ms | projects:",
        list.length
      );
    } catch (e) {
      if (!projectsLoadedRef.current) {
        toast.error("Gagal memuat proyek");
      } else {
        console.warn("[Dashboard] Gagal memperbarui daftar proyek:", e);
      }
    } finally {
      if (isFirstLoad) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createProject = async () => {
    setPresetDialogOpen(true);
  };

  const confirmCreateProject = async () => {
    const preset = presetSettings[selectedPreset] || presetSettings["44"];
    setCreating(true);
    try {
      const res = await api.post("/projects", { name: language === "en" ? "New Project" : "Proyek Baru" });
      setPresetDialogOpen(false);
      toast.success(language === "en" ? "Project created" : "Proyek dibuat");
      navigate(`/editor/${res.data.id}?innerD=${preset.innerD}&outerD=${preset.outerD}&gridPreset=${selectedPreset}&gapH=${preset.gapH ?? 0}&gapV=${preset.gapV ?? 3}&showCenter=${preset.showCenter}&centerDotSize=${preset.centerDotSize}&centerDotColor=%23e22020`);
    } catch (e) {
      toast.error(language === "en" ? "Failed to create project" : "Gagal membuat proyek");
    } finally {
      setCreating(false);
    }
  };

  const doRename = async () => {
    try {
      await api.put(`/projects/${renameTarget.id}`, { name: renameValue });
      setRenameTarget(null);
      load();
      toast.success("Nama diperbarui");
    } catch (e) {
      toast.error("Gagal mengganti nama");
    }
  };

  const doDuplicate = async (id) => {
    try {
      await api.post(`/projects/${id}/duplicate`);
      load();
      toast.success("Proyek diduplikasi");
    } catch (e) {
      toast.error("Gagal menduplikasi");
    }
  };

  const toggleProjectSelection = (id) => {
    setSelectedProjectIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    const visibleIds = filtered.map((p) => p.id);
    const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedProjectIds.includes(id));
    setSelectedProjectIds((prev) =>
      allSelected ? prev.filter((id) => !visibleIds.includes(id)) : [...new Set([...prev, ...visibleIds])]
    );
  };

  const doBulkDelete = async () => {
    setBulkDeleting(true);
    const ids = [...selectedProjectIds];
    const results = await Promise.allSettled(ids.map((id) => api.delete(`/projects/${id}`)));
    const deletedIds = ids.filter((id, i) => results[i].status === "fulfilled");
    setProjects((prev) => prev.filter((p) => !deletedIds.includes(p.id)));
    setSelectedProjectIds((prev) => prev.filter((id) => !deletedIds.includes(id)));
    setBulkDeleting(false);
    setBulkDeleteOpen(false);
    if (deletedIds.length) toast.success(`${deletedIds.length} proyek berhasil dihapus`);
    if (deletedIds.length < ids.length) toast.error(`${ids.length - deletedIds.length} proyek gagal dihapus`);
  };

  const doDelete = async () => {
    try {
      await api.delete(`/projects/${deleteTarget.id}`);
      setDeleteTarget(null);
      load();
      toast.success("Proyek dihapus");
    } catch (e) {
      toast.error("Gagal menghapus");
    }
  };

  const filtered = projects.filter((p) =>
    (p.name || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className={`relative isolate min-h-screen overflow-hidden transition-colors duration-300 ${
      document.documentElement.classList.contains("dark")
        ? "bg-[#030712] text-slate-100"
        : "bg-gradient-to-br from-blue-50 via-white to-cyan-50 text-slate-900"
    }`}>
      {document.documentElement.classList.contains("dark") && (
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute -left-40 -top-32 h-96 w-96 rounded-full bg-blue-600/10 blur-3xl" />
          <div className="absolute -right-40 -top-20 h-[38rem] w-[38rem] rounded-full bg-violet-700/20 blur-[110px]" />
          <div className="absolute left-[28%] top-[32%] h-[38rem] w-[38rem] rounded-full bg-blue-700/20 blur-[110px]" />
          <div className="absolute bottom-0 left-1/3 h-80 w-80 rounded-full bg-violet-600/10 blur-3xl" />
        </div>
      )}
      {/* Top bar */}
      <header className="sticky top-0 z-30 border-b border-blue-900 bg-[#101d3a] text-white shadow-lg shadow-blue-950/30">
        <div className="flex w-full items-center justify-between gap-3 px-6 py-2 sm:px-8">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="pms-workspace-brand">PIN Maker <span className="pms-workspace-brand__studio">Studio</span></span>
          </div>
          <div className="flex items-center gap-3">
            <ExpiryBadge className="hidden sm:inline-flex" />
            {user?.label && (
              <Badge variant="secondary" className="hidden sm:inline-flex" data-testid="workspace-label">
                {user.label}
              </Badge>
            )}
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="relative h-9 w-9 rounded-xl border-blue-400/60 bg-[#1b2b4b] text-blue-100 hover:border-cyan-400 hover:bg-[#263b60] hover:text-white"
              title={language === "en" ? "Notifications" : "Notifikasi"}
              aria-label={language === "en" ? "Notifications" : "Notifikasi"}
              onClick={() => setOrderUpdatesOpen(true)}
            >
              <Bell className="h-4 w-4 text-blue-400" />
              {(unreadNotificationCount > 0 || (lastOrder && String(lastOrder.status || "").toLowerCase() !== "paid")) && (
                <span className={`absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full border border-[#101d3a] ${unreadNotificationCount > 0 ? "bg-red-500" : "bg-amber-400"}`} />
              )}
            </Button>
            <DropdownMenu
              open={settingsMenuOpen}
              onOpenChange={handleSettingsMenuOpenChange}
            >
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="h-9 w-9 rounded-xl border-blue-400/60 bg-[#1b2b4b] text-blue-100 hover:border-cyan-400 hover:bg-[#263b60] hover:text-white"
                  title={language === "en" ? "Settings" : "Pengaturan"} aria-label={language === "en" ? "Settings" : "Pengaturan"}>
                  <svg
                    viewBox="0 0 24 24"
                    className="h-[22px] w-[22px] text-blue-100 transition-transform duration-1500 ease-in-out"
                    style={{ transform: `rotate(${settingsGearRotation}deg)` }}
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M12,15.5A3.5,3.5 0 1,1 15.5,12A3.5,3.5 0 0,1 12,15.5M19.43,12.98C19.47,12.66 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11.02L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.97 19.04,5.05L16.56,6.05C16.04,5.66 15.47,5.34 14.85,5.11L14.48,2.48C14.44,2.24 14.23,2.06 13.98,2.06H10.02C9.77,2.06 9.56,2.24 9.52,2.48L9.15,5.11C8.53,5.34 7.96,5.66 7.44,6.05L4.96,5.05C4.73,4.97 4.46,5.05 4.34,5.27L2.34,8.73C2.22,8.95 2.27,9.22 2.46,9.37L4.57,11.02C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.66 4.57,12.98L2.46,14.63C2.27,14.78 2.22,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.96,18.95L7.44,17.95C7.96,18.34 8.53,18.66 9.15,18.89L9.52,21.52C9.56,21.76 9.77,21.94 10.02,21.94H13.98C14.23,21.94 14.44,21.76 14.48,21.52L14.85,18.89C15.47,18.66 16.04,18.34 16.56,17.95L19.04,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.98Z"
                    />
                  </svg>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={12} style={{
                backgroundColor: theme === "dark" ? "#07132e" : "#f0f6ff",
                borderColor: theme === "dark" ? "rgba(96,165,250,0.25)" : "#bfdbfe",
                color: theme === "dark" ? "#f1f5f9" : "#0f172a",
              }}
            className={`w-60 rounded-2xl border p-2 shadow-xl shadow-blue-950/40 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=open]:slide-in-from-top-2 duration-200 ${
                theme === "dark"
                  ? "border-blue-400/20 bg-[#07132e] text-slate-100"
                  : "border-blue-200 bg-[#f0f6ff] text-slate-900"
              }`}>
<DropdownMenuItem className={`cursor-pointer rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                    theme === "dark"
                      ? "text-slate-100 focus:bg-[#152541] focus:text-white"
                      : "text-slate-800 focus:bg-blue-100 focus:text-blue-950"
                  }`} onClick={() => {  setThemeSettingsOpen(true); }}>
                  <><Palette className="mr-2 h-4 w-4 text-violet-400" />{language === "en" ? "Theme Settings" : "Pengaturan Tema"}</>
                </DropdownMenuItem>
                <DropdownMenuItem className={`cursor-pointer rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                    theme === "dark"
                      ? "text-slate-100 focus:bg-[#152541] focus:text-white"
                      : "text-slate-800 focus:bg-blue-100 focus:text-blue-950"
                  }`} onClick={() => {
                  
                  setDeleteMode((active) => {
                    if (active) setSelectedProjectIds([]);
                    return !active;
                  });
                }}>
                  <><Trash2 className="mr-2 h-4 w-4 text-rose-400" />{deleteMode
                    ? (language === "en" ? "Exit Delete Mode" : "Keluar dari Mode Hapus")
                    : (language === "en" ? "Delete Projects" : "Hapus Proyek")}</>
                </DropdownMenuItem>
                <DropdownMenuItem className={`cursor-pointer rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                    theme === "dark"
                      ? "text-slate-100 focus:bg-[#152541] focus:text-white"
                      : "text-slate-800 focus:bg-blue-100 focus:text-blue-950"
                  }`} onClick={() => {  setLanguageSettingsOpen(true); }}>
                  <><Languages className="mr-2 h-4 w-4 text-emerald-400" />{language === "en" ? "Language Settings" : "Pengaturan Bahasa"}</>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button data-testid="header-new-project-button" onClick={createProject}
              disabled={creating} className="h-9 isolate overflow-hidden rounded-xl border border-white/10 bg-blue-600 font-semibold text-white shadow-none transition-colors hover:brightness-105">
              <Plus className="mr-2 h-4 w-4" /> {language === "en" ? "New Project" : "Proyek Baru"}
            </Button>
            {installPrompt && (
              <Button variant="outline" className="h-9 rounded-xl border-slate-600 bg-[#1b2b4b] text-blue-100 hover:border-cyan-400 hover:bg-[#263b60] hover:text-white" onClick={handleInstall}
                data-testid="pwa-install-button">
                <Download className="mr-2 h-4 w-4" /> Install App
              </Button>
            )}
            <Button variant="outline" size="icon" className="h-9 w-9 rounded-xl border-blue-400/60 bg-[#1b2b4b] text-blue-100 hover:border-blue-300 hover:bg-[#3a285f] hover:text-white"
              onClick={() => setLogoutConfirmOpen(true)} data-testid="logout-button" title="Keluar">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold">{language === "en" ? "Your Projects" : "Proyek Anda"}</h1>
            <p className="text-sm text-muted-foreground">{language === "en" ? "Manage your pin sheets and continue editing." : "Kelola lembar pin dan lanjutkan mengedit."}</p>
          </div>
          <div className="flex items-center gap-3">
            <Input
              data-testid="dashboard-search-input"
              placeholder={language === "en" ? "Search projects…" : "Cari proyek…"}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`w-full sm:w-72 ${
                theme === "dark"
                  ? "!border-blue-400/60 focus-visible:!ring-blue-400/40"
                  : "!border-blue-300 focus-visible:!ring-blue-400/40"
              }`}
            />
          </div>
        </div>

        {deleteMode && selectedProjectIds.length > 0 && (
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
            <Button type="button" variant="outline" className="rounded-xl" onClick={toggleSelectAll}
              disabled={loading || filtered.length === 0} data-testid="select-all-projects">
              <CheckSquare className="mr-2 h-4 w-4" />
              {filtered.length > 0 && filtered.every((p) => selectedProjectIds.includes(p.id))
                ? (language === "en" ? "Deselect All" : "Batalkan Pilihan") : (language === "en" ? "Select All" : "Pilih Semua")}
            </Button>
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">{language === "en" ? `${selectedProjectIds.length} projects selected` : `${selectedProjectIds.length} proyek dipilih`}</span>
              <Button type="button" variant="outline" className="rounded-xl"
                onClick={() => { setDeleteMode(false); setSelectedProjectIds([]); }}
                data-testid="cancel-delete-mode">
                {language === "en" ? "Cancel" : "Batalkan"}
              </Button>
              <Button type="button" variant="destructive" className="rounded-xl"
                onClick={() => setBulkDeleteOpen(true)} data-testid="bulk-delete-button">
                <Trash2 className="mr-2 h-4 w-4" /> {language === "en" ? "Delete Selected" : "Hapus Terpilih"}
              </Button>
            </div>
          </div>
        )}

        <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {loading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-80 rounded-2xl" />
            ))
          ) : filtered.length === 0 ? (
            <div className={`col-span-full flex flex-col items-center justify-center rounded-2xl border border-dashed py-20 text-center backdrop-blur-sm ${
                document.documentElement.classList.contains("dark")
                  ? "border-slate-700/50 bg-[#101827]/55 text-slate-200"
                  : "border-blue-200/70 bg-white/55 text-slate-700"
              }`}>
              <FileText className="h-10 w-10 text-muted-foreground" />
              <h3 className="mt-4 font-display text-lg font-semibold">{language === "en" ? "No projects yet" : "Belum ada proyek"}</h3>
              <Button className="mt-6 isolate overflow-hidden rounded-xl border border-white/10 bg-blue-600 font-semibold text-white shadow-none transition-colors hover:brightness-105" onClick={createProject} data-testid="dashboard-empty-new-button">
                <Plus className="mr-2 h-4 w-4" /> {language === "en" ? "Create Your First Project" : "Buat Proyek Pertama"}
              </Button>
            </div>
          ) : (
            filtered.map((p) => (
              <Card key={p.id} data-testid="dashboard-project-card"
                className="group overflow-hidden rounded-2xl border border-blue-100/90 bg-white text-slate-900 shadow-[0_4px_20px_rgba(59,130,246,0.06)] transition-all duration-300 hover:-translate-y-1 hover:border-cyan-200 hover:shadow-[0_14px_34px_rgba(59,130,246,0.14)]">
                <div
                  className="relative flex cursor-pointer items-center justify-center overflow-hidden border-b border-slate-200 bg-white p-3"
                  style={{ aspectRatio: "210/297" }}
                  onClick={() => navigate(`/editor/${p.id}`)}
                  data-testid="dashboard-project-open-button"
                >
                  <ProjectThumb project={p} />
                  {deleteMode && (
                    <label className="absolute left-3 top-3 z-10 flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-900 shadow-sm"
                      onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={selectedProjectIds.includes(p.id)}
                        onChange={() => toggleProjectSelection(p.id)}
                        aria-label={`${language === "en" ? "Select project" : "Pilih proyek"} ${p.name}`}
                        data-testid={`select-project-${p.id}`}
                        className="h-4 w-4 accent-primary" />
                      {language === "en" ? "Select" : "Pilih"}
                    </label>
                  )}
                </div>
                <div className="flex items-center justify-between p-4">
                  <div className="min-w-0">
                    <h3 className="truncate font-display font-semibold">{p.name}</h3>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      <span className="font-mono-num">{p.filled_count}</span>/{p.capacity} {language === "en" ? "filled" : "terisi"} ·{" "}
                      <span className="font-mono-num">{p.page_count}</span> {language === "en" ? "pages" : "halaman"}
                    </p>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="rounded-xl"
                        data-testid="dashboard-project-menu-button"
                        onClick={(e) => e.stopPropagation()}>
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      className="min-w-[170px] rounded-xl border border-blue-200 bg-white p-1 text-slate-800 shadow-xl shadow-blue-950/10 dark:border-blue-800 dark:bg-[#101d3a] dark:text-slate-100 dark:shadow-blue-950/30"
                    >
                      <DropdownMenuItem
                        className="cursor-pointer rounded-lg text-slate-700 focus:bg-blue-50 focus:text-blue-900 dark:text-slate-100 dark:focus:bg-[#263b60] dark:focus:text-white"
                        onClick={() => navigate(`/editor/${p.id}`)}
                      >
                        <Pencil className="mr-2 h-4 w-4" /> Buka
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="cursor-pointer rounded-lg text-slate-700 focus:bg-blue-50 focus:text-blue-900 dark:text-slate-100 dark:focus:bg-[#263b60] dark:focus:text-white"
                        onClick={() => { setRenameTarget(p); setRenameValue(p.name); }}
                      >
                        <Pencil className="mr-2 h-4 w-4" /> Ganti nama
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="cursor-pointer rounded-lg text-slate-700 focus:bg-blue-50 focus:text-blue-900 dark:text-slate-100 dark:focus:bg-[#263b60] dark:focus:text-white"
                        onClick={() => doDuplicate(p.id)}
                      >
                        <Copy className="mr-2 h-4 w-4" /> Duplikat
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="cursor-pointer rounded-lg text-red-600 focus:bg-red-50 focus:text-red-700 dark:text-red-400 dark:focus:bg-[#263b60] dark:focus:text-red-300"
                        onClick={() => setDeleteTarget(p)}
                      >
                        <Trash2 className="mr-2 h-4 w-4" /> Hapus
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </Card>
            ))
          )}
        </div>
      </main>

      <AlertDialog open={logoutConfirmOpen} onOpenChange={setLogoutConfirmOpen}>
        <AlertDialogContent data-testid="dashboard-logout-dialog" className="max-w-sm rounded-2xl border border-blue-200 bg-[#f0f6ff] text-slate-900 shadow-2xl shadow-blue-950/20 dark:border-blue-800 dark:bg-[#101d36] dark:text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-bold text-slate-900 dark:text-white">
              {language === "en" ? "Log out?" : "Yakin ingin keluar?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600 dark:text-blue-200/75">
              {language === "en"
                ? "Are you sure you want to log out of PIN Maker Studio?"
                : "Apakah kamu yakin ingin keluar dari PIN Maker Studio?"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="rounded-xl border border-blue-200 bg-white text-slate-700 hover:bg-blue-50 hover:text-blue-900 dark:border-blue-700 dark:bg-[#1b2b4b] dark:text-blue-100 dark:hover:bg-[#263b60] dark:hover:text-white">
              {language === "en" ? "Cancel" : "Batal"}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={logout}
              className="rounded-xl border border-red-400 bg-red-600 font-semibold text-white shadow-none transition-colors hover:bg-red-700 focus-visible:ring-2 focus-visible:ring-red-400 dark:border-red-500/60 dark:bg-red-600 dark:hover:bg-red-700"
            >
              {language === "en" ? "Yes, log out" : "Ya, Keluar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      

      </AlertDialog>

      {/* API Key renewal dialog - active key users remain on Dashboard */}
      <Dialog open={renewalModalOpen} onOpenChange={setRenewalModalOpen}>
        <DialogContent className={`w-[calc(100vw-2rem)] max-w-md rounded-2xl border p-5 ${
          theme === "dark"
            ? "border-blue-400/25 bg-[#07132e] text-white"
            : "border-blue-200 bg-gradient-to-br from-white via-blue-50 to-violet-50 text-slate-900"
        }`}>
          <DialogHeader>
            <DialogTitle>{language === "en" ? "Renew your API Key" : "Perpanjang API Key"}</DialogTitle>
            <DialogDescription className={theme === "dark" ? "text-slate-400" : "text-slate-600"}>
              {language === "en"
                ? "Verify the owner email. Your existing API Key and saved projects will remain the same."
                : "Verifikasi email pemilik. API Key lama dan proyek yang tersimpan akan tetap sama."}
            </DialogDescription>
          </DialogHeader>

          {renewalStep === "email" && (
            <div className="space-y-3">
              {renewalEmailRequired && (
                <div className="space-y-1.5">
                  <label htmlFor="dashboard-renewal-email" className="text-sm font-medium">
                    {language === "en" ? "Owner email address" : "Email pemilik API Key"}
                  </label>
                  <Input
                    id="dashboard-renewal-email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    maxLength={254}
                    value={renewalEmailInput}
                    onChange={(e) => setRenewalEmailInput(e.target.value)}
                    placeholder="nama@email.com"
                  />
                  <p className="text-xs text-muted-foreground">
                    {language === "en"
                      ? "This Admin-created key has no owner email. It is saved only after OTP verification."
                      : "API Key yang dibuat Admin belum memiliki email pemilik. Email disimpan setelah OTP berhasil diverifikasi."}
                  </p>
                </div>
              )}
              {renewalBusy && <p className="text-sm text-muted-foreground">{language === "en" ? "Sending verification code..." : "Mengirim kode verifikasi..."}</p>}
              {renewalEmailRequired && (
                <Button
                  className="w-full"
                  disabled={renewalBusy || !renewalEmailInput.trim()}
                  onClick={() => beginDashboardRenewalVerification(renewalEmailInput)}
                >
                  {language === "en" ? "Send verification code" : "Kirim Kode Verifikasi"}
                </Button>
              )}
            </div>
          )}

          {renewalStep === "otp" && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {language === "en"
                  ? `Enter the 6-digit code sent to ${renewalEmailHint}.`
                  : `Masukkan kode 6 angka yang dikirim ke ${renewalEmailHint}.`}
              </p>
              <Input
                value={renewalOtp}
                onChange={(e) => setRenewalOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="000000"
                className="h-12 text-center text-xl font-bold tracking-[0.35em]"
              />
              <Button className="w-full" disabled={renewalBusy || renewalOtp.length !== 6} onClick={verifyDashboardRenewalOtp}>
                {renewalBusy ? (language === "en" ? "Verifying..." : "Memverifikasi...") : (language === "en" ? "Verify code" : "Verifikasi Kode")}
              </Button>
              <button
                type="button"
                disabled={renewalBusy}
                onClick={() => beginDashboardRenewalVerification(renewalEmailInput)}
                className="text-sm text-blue-600 underline disabled:opacity-50 dark:text-blue-300"
              >
                {language === "en" ? "Resend code" : "Kirim ulang kode"}
              </button>
            </div>
          )}

          {renewalStep === "plans" && (
            <div className="space-y-3">
              <p className="text-sm font-semibold">{language === "en" ? "Choose your renewal package" : "Pilih paket perpanjangan"}</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {["starter", "pro"].map((planId) => {
                  const details = planId === "starter"
                    ? dashboardPlanPricing("starter", 50000, 30)
                    : dashboardPlanPricing("pro", 120000, 90);
                  return (
                    <button
                      key={planId}
                      type="button"
                      disabled={renewalBusy}
                      onClick={() => startDashboardRenewalCheckout(planId)}
                      className="rounded-xl border border-blue-400/30 bg-blue-500/10 p-4 text-left transition hover:border-blue-500 hover:bg-blue-500/15 disabled:opacity-50"
                    >
                      <span className="block font-bold">{planId === "starter" ? "Starter" : "Pro"}</span>
                      {details.discountActive && <span className="mt-1 block text-xs text-muted-foreground line-through">{dashboardRupiah(details.originalPrice)}</span>}
                      <span className="mt-1 block text-xl font-extrabold text-blue-600 dark:text-cyan-400">{dashboardRupiah(details.price)}</span>
                      <span className="mt-1 block text-xs text-muted-foreground">{details.days} {language === "en" ? "days" : "hari"}</span>
                    </button>
                  );
                })}
              </div>
              {renewalBusy && <p className="text-center text-sm text-muted-foreground">{language === "en" ? "Preparing payment..." : "Menyiapkan pembayaran..."}</p>}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setRenewalModalOpen(false)}>
              {language === "en" ? "Close" : "Tutup"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notifications dialog */}
      <Dialog open={orderUpdatesOpen} onOpenChange={setOrderUpdatesOpen}>
        <DialogContent className={`w-[calc(100vw-2rem)] max-w-sm rounded-2xl border p-4 shadow-2xl transition-colors duration-300 ${
            theme === "dark"
              ? "border-blue-400/25 bg-[#07132e] text-white shadow-[0_16px_48px_rgba(0,0,0,0.38)]"
              : "border-blue-200 bg-gradient-to-br from-white via-blue-50 to-violet-50 text-slate-900 shadow-[0_16px_48px_rgba(30,64,175,0.12)]"
          }`}>
          <DialogHeader>
            <DialogTitle className={`text-base font-bold ${theme === "dark" ? "text-white" : "text-slate-900"}`}>
              {language === "en" ? "Notifications" : "Notifikasi"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            {notifications.length === 0 && !lastOrderId && (
              <div className={`rounded-xl border p-3 text-sm ${
                theme === "dark"
                  ? "border-blue-400/20 bg-[#0b1733] text-slate-300"
                  : "border-blue-200 bg-white/90 text-slate-600"
              }`}>
                {language === "en" ? "No notifications yet." : "Belum ada notifikasi."}
              </div>
            )}

            <div className="flex justify-end">
                {notifications.length > 0 && (
                  <button type="button" onClick={clearAllNotifications} className="text-xs font-semibold text-rose-600 underline hover:text-rose-800 dark:text-rose-300 dark:hover:text-rose-200">
                    {language === "en" ? "Clear all" : "Bersihkan Semua"}
                  </button>
                )}
              </div>
              {notifications.map((item) => {
                            const statusText = `${item.status || ""} ${item.type || ""} ${item.action || ""}`.toLowerCase();
                            const isRed = /expired|cancelled|canceled|kedaluwarsa|failed|gagal/.test(statusText);
                            const isGreen = !isRed && /paid|success|berhasil/.test(statusText);
                            const cardTone = isRed
                              ? (theme === "dark" ? "border-rose-400/30 bg-rose-950/25" : "border-rose-200 bg-rose-50/80")
                              : isGreen
                                ? (theme === "dark" ? "border-emerald-400/30 bg-emerald-950/20" : "border-emerald-200 bg-emerald-50/80")
                                : (theme === "dark" ? "border-blue-400/30 bg-[#122447]" : "border-blue-200 bg-white/95");
                            const titleTone = isRed ? "text-rose-600 dark:text-rose-300" : isGreen ? "text-emerald-700 dark:text-emerald-300" : "";
                            const messageTone = isRed ? "text-rose-700 dark:text-rose-200" : isGreen ? "text-emerald-800 dark:text-emerald-200" : (theme === "dark" ? "text-slate-300" : "text-slate-600");
                            const iconTone = isRed ? "text-rose-500" : isGreen ? "text-emerald-500" : "text-blue-500";
                            const dotTone = isRed ? "bg-rose-500" : isGreen ? "bg-emerald-500" : "bg-blue-500";
                            return (
                              <div key={item.id} className={`rounded-xl border p-3 ${cardTone} ${item.read_at ? "opacity-70" : ""}`}>
                                <div className="flex items-start gap-2">
                                  <Bell className={`mt-0.5 h-4 w-4 shrink-0 ${iconTone}`} />
                                  <div className="min-w-0 flex-1">
                                    <p className={`text-sm font-semibold ${titleTone}`}>{item.title || (item.plan_name || (language === "en" ? "Notification" : "Notifikasi"))}</p>
                                    {item.message && <p className={`mt-1 text-sm ${messageTone}`}>{item.message}</p>}
                                    {!item.message && item.status && <p className={`mt-1 text-sm ${titleTone}`}>{item.status}</p>}
                                    <p className={`mt-1 text-xs ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>
                                      {item.created_at ? new Date(item.created_at).toLocaleString(language === "en" ? "en-US" : "id-ID") : ""}
                                    </p>
                                  </div>
                                  {!item.read_at && <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${dotTone}`} />}
                                </div>
                                <div className="mt-2 flex flex-wrap gap-3">
                                  {item.action === "renewal" && (
                                    <button type="button" className="text-xs font-semibold text-blue-700 underline hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-100" onClick={() => startRenewalFromNotification(item.id)}>
                                      {language === "en" ? "Renew API Key" : "Perpanjang API Key"}
                                    </button>
                                  )}
                                  {item.action === "payment_pending" && (
                                    <button type="button" className="text-xs font-semibold text-blue-700 underline hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-100" onClick={() => resumePendingRenewalPayment(item)}>
                                      {language === "en" ? "Resume Payment" : "Lanjutkan Pembayaran"}
                                    </button>
                                  )}
                                  {!item.read_at && (
                                    <button type="button" className="text-xs font-medium text-slate-600 underline hover:text-slate-900 dark:text-slate-300 dark:hover:text-white" onClick={() => markNotificationRead(item.id)}>
                                      {language === "en" ? "Mark as read" : "Tandai sudah dibaca"}
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}

            {lastOrderId && (
              <div className={`space-y-3 rounded-xl border p-3 ${
                theme === "dark"
                  ? "border-blue-400/20 bg-[#0b1733] text-slate-100"
                  : "border-blue-200 bg-white/90 text-slate-900"
              }`}>
                <div className="flex items-start gap-3">
                  <div className="rounded-full bg-blue-500/10 p-2 text-blue-400"><Bell className="h-4 w-4" /></div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold">{lastOrder ? orderStatusText(lastOrder.status) : (language === "en" ? "Checking order status..." : "Memeriksa status pesanan...")}</p>
                    <p className={`mt-1 break-all text-xs ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>Order ID: {lastOrderId}</p>
                    {lastOrder?.plan_name && <p className={`mt-1 text-sm ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>{language === "en" ? "Package" : "Paket"}: {lastOrder.plan_name}</p>}
                  </div>
                </div>
                {lastOrder?.status === "paid" ? (
                  <div className={`rounded-lg border p-3 text-sm ${theme === "dark" ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-200" : "border-emerald-200 bg-emerald-50 text-emerald-800"}`}>
                    {lastOrder.order_type === "renewal"
                      ? (language === "en" ? "Your existing API Key has been renewed. The key and project data remain the same." : "API Key lama berhasil diperpanjang. API Key dan data proyek tetap sama.")
                      : (language === "en" ? "Payment has been verified by the server. Check the purchase email, including the spam folder, for your API Key." : "Pembayaran telah diverifikasi server. Periksa email pembelian, termasuk folder spam, untuk menemukan API Key.")}
                  </div>
                ) : ["failed", "cancel", "cancelled", "expire", "expired", "checkout_error"].includes(String(lastOrder?.status || "").toLowerCase()) ? (
                  <div className={`rounded-lg border p-3 text-sm ${theme === "dark" ? "border-red-500/20 bg-red-500/10 text-red-200" : "border-red-200 bg-red-50 text-red-800"}`}>
                    {language === "en" ? "This payment was not completed. Return to the purchase page to try again." : "Pembayaran ini belum berhasil. Kembali ke halaman pembelian untuk mencoba lagi."}
                  </div>
                ) : (
                  <div className={`rounded-lg border p-3 text-sm ${theme === "dark" ? "border-blue-500/20 bg-blue-500/10 text-blue-200" : "border-blue-200 bg-blue-50 text-blue-800"}`}>
                    {language === "en" ? "Waiting for server confirmation. This status refreshes automatically." : "Menunggu konfirmasi server. Status ini diperbarui secara otomatis."}
                  </div>
                )}
                {orderStatusError && <p className="text-xs text-amber-500">{language === "en" ? "The latest status could not be retrieved. It will be checked again automatically." : "Status terbaru belum dapat diambil. Sistem akan mencoba kembali secara otomatis."}</p>}
                {orderStatusLoading && !lastOrder && <p className="text-xs text-muted-foreground">{language === "en" ? "Loading order..." : "Memuat pesanan..."}</p>}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOrderUpdatesOpen(false)}>
              {language === "en" ? "Close" : "Tutup"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={presetSettingsOpen} onOpenChange={setPresetSettingsOpen}>
        <DialogContent className="max-h-[90vh] w-[95vw] max-w-5xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{language === "en" ? <>Pin Preset <span className="text-blue-400">Settings</span></> : <>Pengaturan Preset <span className="text-blue-400">Pin</span></>}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium">Ukuran preset</label>
              <div className="grid grid-cols-5 gap-2">
                {["25", "32", "44", "58", "75"].map((size) => (
                  <Button key={size} type="button"
                    variant={selectedPreset === size ? "default" : "outline"}
                    className="px-2" onClick={() => setSelectedPreset(size)}>
                    {size}
                  </Button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-sm">Diameter dalam (mm)</label>
                <div className="pms-number-stepper relative">
                  <Input type="number" min="1" step="0.1" className="pr-3 [appearance:textfield] [-moz-appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    value={presetSettings[selectedPreset].innerD}
                    onChange={(e) => updatePresetSetting(selectedPreset, "innerD", Number(e.target.value))} />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-sm">Diameter luar (mm)</label>
                <div className="pms-number-stepper relative">
                  <Input type="number" min="1" step="0.1" className="pr-3 [appearance:textfield] [-moz-appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    value={presetSettings[selectedPreset].outerD}
                    onChange={(e) => updatePresetSetting(selectedPreset, "outerD", Number(e.target.value))} />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-sm">Jarak X / horizontal (mm)</label>
                <div className="pms-number-stepper relative">
                  <Input type="number" min="0" step="0.5" className="pr-3 [appearance:textfield] [-moz-appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    value={presetSettings[selectedPreset].gapH}
                    onChange={(e) => updatePresetSetting(selectedPreset, "gapH", Math.max(0, Number(e.target.value)))} />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-sm">Jarak Y / vertikal (mm)</label>
                <div className="pms-number-stepper relative">
                  <Input type="number" min="0" step="0.5" className="pr-3 [appearance:textfield] [-moz-appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    value={presetSettings[selectedPreset].gapV}
                    onChange={(e) => updatePresetSetting(selectedPreset, "gapV", Math.max(0, Number(e.target.value)))} />
                </div>
              </div>
            </div>
            <div className="space-y-3 rounded-xl border p-3">
              <div className="flex items-center gap-3">
                <input id="preset-red-dot" type="checkbox"
                  className="h-4 w-4 accent-red-600"
                  checked={presetSettings[selectedPreset].showCenter}
                  onChange={(e) => updatePresetSetting(selectedPreset, "showCenter", e.target.checked)} />
                <label htmlFor="preset-red-dot" className="text-sm font-medium">
                  Aktifkan dot merah di atas lingkaran
                </label>
              </div>
              <div className="space-y-1">
                <label className="text-sm">Ukuran dot merah (mm)</label>
                <div className="pms-number-stepper relative">
                  <Input type="number" min="0.1" step="0.1" className="pr-3 [appearance:textfield] [-moz-appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    value={presetSettings[selectedPreset].centerDotSize}
                    disabled={!presetSettings[selectedPreset].showCenter}
                    onChange={(e) => updatePresetSetting(selectedPreset, "centerDotSize", Number(e.target.value))} />
                </div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Pengaturan tersimpan otomatis untuk setiap ukuran pin.
            </p>
          </div>
          <DialogFooter>
            <Button className="pms-preset-done-button" style={{ backgroundColor: "#2563eb", color: "#ffffff", border: "1px solid #3b82f6" }} onClick={() => setPresetSettingsOpen(false)}>{language === "en" ? "Done" : "Selesai"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={themeSettingsOpen} onOpenChange={setThemeSettingsOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{language === "en" ? "Theme Settings" : "Pengaturan Tema"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Button variant={theme === "light" ? "default" : "outline"} onClick={() => setTheme("light")}>{language === "en" ? "Light" : "Terang"}</Button>
            <Button variant={theme === "dark" ? "default" : "outline"} onClick={() => setTheme("dark")}>{language === "en" ? "Dark" : "Gelap"}</Button>
          </div>
          <DialogFooter><Button onClick={() => setThemeSettingsOpen(false)}>{language === "en" ? "Done" : "Selesai"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={languageSettingsOpen} onOpenChange={setLanguageSettingsOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{language === "en" ? "Language Settings" : "Pengaturan Bahasa"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Button variant={language === "id" ? "default" : "outline"} onClick={() => setLanguage("id")}>Bahasa Indonesia</Button>
            <Button variant={language === "en" ? "default" : "outline"} onClick={() => setLanguage("en")}>English</Button>
          </div>
          <p className="text-xs text-muted-foreground">{language === "en" ? "Your language preference is saved in this browser." : "Pilihan bahasa tersimpan di browser."}</p>
          <DialogFooter><Button onClick={() => setLanguageSettingsOpen(false)}>{language === "en" ? "Done" : "Selesai"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New project size preset dialog */}
      <Dialog open={presetDialogOpen} onOpenChange={(open) => !creating && setPresetDialogOpen(open)}>
        <DialogContent
          data-testid="dashboard-pin-size-dialog"
          className={`w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto overflow-x-hidden isolate rounded-2xl border p-5 shadow-xl transition-colors duration-300 sm:max-w-lg sm:p-6 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 ${
            document.documentElement.classList.contains("dark")
              ? "border-blue-400/25 bg-[#07132e] text-white shadow-[0_16px_48px_rgba(0,0,0,0.38)]"
              : "border-blue-200 bg-gradient-to-br from-white via-blue-50 to-violet-50 text-slate-900 shadow-[0_16px_48px_rgba(30,64,175,0.12)]"
          }`}
        >
          <DialogHeader>
            <DialogTitle className="text-xl font-extrabold tracking-tight">
              <span className={document.documentElement.classList.contains("dark") ? "text-white" : "text-slate-900"}>
                {language === "en" ? "Choose " : "Pilih ukuran "}
              </span>
              <span className={document.documentElement.classList.contains("dark") ? "text-white" : "text-slate-900"}>
                {language === "en" ? "pin size" : "pin"}
              </span>
            </DialogTitle>
            <DialogDescription className={document.documentElement.classList.contains("dark") ? "text-slate-400" : "text-slate-500"}>
              {language === "en"
                ? "Choose the pin size that fits your project."
                : "Tentukan ukuran pin yang sesuai dengan kebutuhan proyekmu."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            {[
              { value: "25", label: "25 mm", detail: "35 pin per A4 · 5 × 7" },
              { value: "32", label: "32 mm", detail: "24 pin per A4 · 4 × 6" },
              { value: "44", label: "44 mm", detail: "15 pin per A4 · 3 × 5" },
              { value: "58", label: "58 mm", detail: "8 pin per A4 · 2 × 4" },
              { value: "75", label: "75 mm", detail: "6 pin per A4 · 2 × 3" },
            ].map((preset) => (
              <button
                key={preset.value}
                type="button"
                onClick={() => setSelectedPreset(preset.value)}
                className={`group overflow-hidden rounded-xl border p-3.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${
                  document.documentElement.classList.contains("dark")
                    ? selectedPreset === preset.value
                      ? "border-blue-300 bg-gradient-to-br from-blue-600/35 via-indigo-600/25 to-violet-600/35 text-white ring-1 ring-blue-300/60 shadow-lg shadow-blue-950/40"
                      : "border-slate-700 bg-[#0b1733]/90 text-white hover:border-blue-400/70 hover:bg-[#142448]"
                    : selectedPreset === preset.value
                      ? "border-blue-500 bg-gradient-to-br from-blue-100 via-indigo-50 to-violet-100 text-slate-900 ring-1 ring-blue-300 shadow-lg shadow-blue-200/60"
                      : "border-blue-200 bg-white/90 text-slate-900 hover:border-indigo-300 hover:bg-indigo-50"
                }`}
                aria-pressed={selectedPreset === preset.value}
              >
                <span className={`block font-bold ${
                  document.documentElement.classList.contains("dark") ? "text-white" : "text-slate-900"
                }`}>{preset.label}</span>
                <span className={`mt-1 block text-sm ${
                  document.documentElement.classList.contains("dark") ? "text-slate-300" : "text-slate-600"
                }`}>{preset.detail}</span>
              </button>
            ))}
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              className={`border ${
                document.documentElement.classList.contains("dark")
                  ? "rounded-xl border-slate-600 bg-slate-900/70 text-slate-200 hover:border-blue-400 hover:bg-slate-800 hover:text-white"
                  : "rounded-xl border-blue-200 bg-white text-slate-700 hover:border-indigo-300 hover:bg-indigo-50 hover:text-slate-900"
              }`}
              onClick={() => setPresetDialogOpen(false)}
              disabled={creating}
            >
              {language === "en" ? "Cancel" : "Batal"}
            </Button>
            <Button
              className="relative isolate overflow-hidden rounded-xl border border-white/10 bg-blue-600 font-semibold text-white shadow-none transition-colors hover:brightness-105"
              onClick={confirmCreateProject}
              disabled={creating}
              data-testid="create-project-confirm-button"
            >
              {creating ? (language === "en" ? "Creating..." : "Membuat...") : (language === "en" ? "Create Project" : "Buat Proyek")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename dialog */}
      <Dialog open={!!renameTarget} onOpenChange={(o) => !o && setRenameTarget(null)}>
        <DialogContent
          className="max-w-md rounded-2xl border p-5 shadow-xl transition-colors duration-300"
          style={{
            backgroundColor: theme === "dark" ? "#07132e" : "#f0f6ff",
            borderColor: theme === "dark" ? "rgba(96,165,250,0.25)" : "#bfdbfe",
            color: theme === "dark" ? "#f1f5f9" : "#0f172a",
          }}
        >
          <DialogHeader><DialogTitle className={theme === "dark" ? "text-white" : "text-slate-900"}>{language === "en" ? "Rename project" : "Ganti nama proyek"}</DialogTitle></DialogHeader>
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            data-testid="rename-input"
            autoFocus
            className={theme === "dark"
              ? "rounded-xl border-blue-400/30 bg-[#0b1733] text-white focus-visible:ring-blue-400"
              : "rounded-xl border-blue-200 bg-white text-slate-900 focus-visible:ring-blue-400"}
          />
          <DialogFooter>
            <Button
              variant="ghost"
              className={theme === "dark"
                ? "rounded-xl text-slate-300 hover:bg-[#152541] hover:text-white"
                : "rounded-xl text-slate-700 hover:bg-blue-100 hover:text-blue-950"}
              onClick={() => setRenameTarget(null)}
            >{language === "en" ? "Cancel" : "Batal"}</Button>
            <Button onClick={doRename} data-testid="rename-save-button" className="rounded-xl bg-blue-600 font-semibold text-white hover:bg-blue-500">{language === "en" ? "Save" : "Simpan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Konfirmasi hapus massal */}
      <AlertDialog open={bulkDeleteOpen} onOpenChange={(open) => {
        if (!open && !bulkDeleting) setBulkDeleteOpen(false);
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{language === "en" ? "Delete selected projects?" : "Hapus proyek terpilih?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {language === "en" ? `${selectedProjectIds.length} projects will be permanently deleted. This action cannot be undone.` : `Sebanyak ${selectedProjectIds.length} proyek akan dihapus permanen. Tindakan ini tidak dapat dibatalkan.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleting}>{language === "en" ? "Cancel" : "Batal"}</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); doBulkDelete(); }}
              disabled={bulkDeleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="bulk-delete-confirm">
              {bulkDeleting ? (language === "en" ? "Deleting..." : "Menghapus...") : (language === "en" ? "Yes, Delete All" : "Ya, Hapus Semua")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirm */}<AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent
          className="max-w-md rounded-2xl border p-5 shadow-xl transition-colors duration-300"
          style={{
            backgroundColor: theme === "dark" ? "#07132e" : "#f0f6ff",
            borderColor: theme === "dark" ? "rgba(96,165,250,0.25)" : "#bfdbfe",
            color: theme === "dark" ? "#f1f5f9" : "#0f172a",
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle className={theme === "dark" ? "text-white" : "text-slate-900"}>{language === "en" ? "Delete project?" : "Hapus proyek?"}</AlertDialogTitle>
            <AlertDialogDescription className={theme === "dark" ? "text-slate-300" : "text-slate-600"}>
              {language === "en" ? `This permanently deletes all images in project "${deleteTarget?.name}".` : `Tindakan ini permanen dan menghapus semua gambar pada proyek "${deleteTarget?.name}".`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className={theme === "dark"
                ? "rounded-xl border-blue-400/30 bg-[#0b1733] text-slate-200 hover:bg-[#152541] hover:text-white"
                : "rounded-xl border-blue-200 bg-white text-slate-700 hover:bg-blue-50"}
            >{language === "en" ? "Cancel" : "Batal"}</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="delete-confirm-button"
              className="rounded-xl border border-red-400 bg-red-600 font-semibold text-white hover:bg-red-700">
              {language === "en" ? "Delete" : "Hapus"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
